import { useState, useEffect, useCallback, useRef } from "react";
import { Upload, X, Loader2, Image as ImageIcon, Library, Pencil, Trash2, Plus } from "lucide-react";
import { imageService } from "@/lib/services/supabaseImageService";
import { apiService } from "@/lib/api-functions/api-service";
import { LoadingSpinner } from "@/components/loaders/loading-spinner";
import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';

interface TabbedFileUploaderComponentProps {
  onFileChange?: (file: File | string | null) => void;
  onLinkChange?: (link: string) => void;
  onImageUpload?: (url: string | string[]) => void;
  onClear?: () => void;
  onBlur?: (value: any) => void;
  accept?: string;
  placeholder?: string;
  uploadText?: string;
  linkPlaceholder?: string;
  gameId?: string;
  value?: string | string[];
  disabled?: boolean;
  multiple?: boolean;
}

interface ImageData {
  name: string;
  path: string;
  url: string;
  size: number;
  createdAt: string;
  updatedAt: string;
  mimetype: string;
}

export const TabbedFileUploaderComponent = ({
  onFileChange,
  onLinkChange,
  onImageUpload,
  onClear,
  onBlur,
  accept = "image/*",
  placeholder = "Upload",
  uploadText = "Click to upload image",
  linkPlaceholder = "Enter image URL",
  gameId,
  value,
  disabled = false,
  multiple = false,
}: TabbedFileUploaderComponentProps) => {
  const { t } = usePlatformTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Normalize value to array for internal handling
  const normalizeValue = (val: string | string[] | undefined): string[] => {
    if (!val) return [];
    if (Array.isArray(val)) return val.filter(Boolean);
    return val ? [val] : [];
  };
  
  const [images, setImages] = useState<string[]>(normalizeValue(value));
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerMode, setPickerMode] = useState<"upload" | "library">("upload");
  const [libraryImages, setLibraryImages] = useState<ImageData[]>([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  // Sync with external value changes
  useEffect(() => {
    setImages(normalizeValue(value));
  }, [value]);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setUploadError(null);
    
    if (file) {
      if (!file.type.startsWith('image/')) {
        setUploadError('Please select an image file (PNG, JPG, etc.)');
        return;
      }
      
      if (!gameId) {
        setUploadError('Game ID is required for image upload');
        return;
      }
      
      setUploading(true);
      try {
        const result = await imageService.uploadImage(file, gameId);
        if (result.success && result.url) {
          const newImages = multiple ? [...images, result.url] : [result.url];
          setImages(newImages);
          setSelectedIndex(multiple ? newImages.length - 1 : 0);
          setShowPicker(false);
          
          notifyChange(newImages);
        } else {
          setUploadError(result.error || 'Upload failed');
        }
      } catch (error) {
        console.error('Upload error:', error);
        setUploadError('Failed to upload image');
      } finally {
        setUploading(false);
      }
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const fetchLibraryImages = useCallback(async () => {
    if (!gameId) return;
    
    setLoadingLibrary(true);
    try {
      const response = await apiService.listImages(gameId);
      if (response.success && response.data) {
        setLibraryImages(response.data.images);
      }
    } catch (error) {
      console.error('Error fetching library images:', error);
    } finally {
      setLoadingLibrary(false);
    }
  }, [gameId]);

  useEffect(() => {
    if (showPicker && pickerMode === 'library' && gameId) {
      fetchLibraryImages();
    }
  }, [showPicker, pickerMode, gameId, fetchLibraryImages]);

  const notifyChange = (newImages: string[]) => {
    const outputValue = multiple ? newImages : (newImages[0] || null);
    
    if (onImageUpload) {
      onImageUpload(outputValue as string | string[]);
    } else if (onFileChange) {
      onFileChange(outputValue as string | null);
    }
    if (onBlur) {
      onBlur(outputValue);
    }
  };

  const handleLibrarySelect = (imageUrl: string) => {
    const newImages = multiple ? [...images, imageUrl] : [imageUrl];
    setImages(newImages);
    setSelectedIndex(multiple ? newImages.length - 1 : 0);
    setShowPicker(false);
    
    notifyChange(newImages);
  };

  const handleRemoveImage = (index: number) => {
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
    
    // Adjust selected index if needed
    if (selectedIndex >= newImages.length) {
      setSelectedIndex(Math.max(0, newImages.length - 1));
    }
    
    notifyChange(newImages);
    
    if (newImages.length === 0 && onClear) {
      onClear();
    }
  };

  const handleSelectImage = (index: number) => {
    setSelectedIndex(index);
  };

  const handleClearAll = () => {
    setImages([]);
    setSelectedIndex(0);
    setUploadError(null);
    
    if (onClear) onClear();
    if (onFileChange) onFileChange(null);
    if (onLinkChange) onLinkChange('');
    if (onImageUpload) onImageUpload(multiple ? [] : '');
  };

  const openPicker = (mode: "upload" | "library") => {
    setPickerMode(mode);
    setShowPicker(true);
    setUploadError(null);
  };

  const currentImage = images[selectedIndex];
  const hasImages = images.length > 0;

  // Render when there are images
  if (hasImages) {
    return (
      <div className="space-y-2">
        {/* Main image display with thumbnails */}
        <div className={`flex gap-3 ${multiple && images.length > 1 ? '' : ''}`}>
          {/* Thumbnails on the left (only for multiple images) */}
          {multiple && images.length > 1 && (
            <div className="flex flex-col gap-2 w-16 flex-shrink-0">
              {images.map((img, index) => (
                <div
                  key={`${img}-${index}`}
                  className={`relative group cursor-pointer rounded-md overflow-hidden border-2 transition-all aspect-square ${
                    index === selectedIndex
                      ? 'border-primary ring-1 ring-primary'
                      : 'border-border hover:border-muted-foreground'
                  }`}
                  onClick={() => !disabled && handleSelectImage(index)}
                >
                  <img
                    src={img}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23ddd" width="100" height="100"/></svg>';
                    }}
                  />
                  {!disabled && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveImage(index);
                      }}
                      className="absolute top-0.5 right-0.5 p-0.5 bg-destructive/90 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X size={10} className="text-destructive-foreground" />
                    </button>
                  )}
                </div>
              ))}
              {/* Add more button */}
              {!disabled && (
                <button
                  onClick={() => openPicker('upload')}
                  className="w-full aspect-square rounded-md border-2 border-dashed border-border hover:border-primary flex items-center justify-center transition-colors"
                >
                  <Plus size={16} className="text-muted-foreground" />
                </button>
              )}
            </div>
          )}
          
          {/* Main/Featured image */}
          <div 
            className="relative flex-1 rounded-lg overflow-hidden "
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <img
              src={currentImage}
              alt="Selected image"
              className="w-full h-auto max-h-64 object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
                const parent = (e.target as HTMLImageElement).parentElement;
                if (parent) {
                  const fallback = parent.querySelector('.image-fallback');
                  if (fallback) (fallback as HTMLElement).style.display = 'flex';
                }
              }}
            />
            <div className="image-fallback hidden items-center justify-center p-8 text-muted-foreground">
              <div className="text-center">
                <ImageIcon size={48} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">{t('platform.editor.tabbedfileuploadercomponent.image_could_not_be_loaded')}</p>
              </div>
            </div>
            
            {/* Overlay actions (only in edit mode) */}
            {!disabled && (isHovering || !currentImage) && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center gap-2 transition-opacity">
                <button
                  onClick={() => openPicker('upload')}
                  className="p-2 bg-white/90 rounded-lg hover:bg-white transition-colors shadow-sm"
                  title="Change image"
                >
                  <Pencil size={18} className="text-gray-700" />
                </button>
                <button
                  onClick={() => openPicker('library')}
                  className="p-2 bg-white/90 rounded-lg hover:bg-white transition-colors shadow-sm"
                  title="Choose from library"
                >
                  <Library size={18} className="text-gray-700" />
                </button>
                {!multiple && (
                  <button
                    onClick={handleClearAll}
                    className="p-2 bg-destructive/90 rounded-lg hover:bg-destructive transition-colors shadow-sm"
                    title="Remove image"
                  >
                    <Trash2 size={18} className="text-destructive-foreground" />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Image picker modal/dropdown */}
        {showPicker && !disabled && (
          <div className="border border-border rounded-lg p-4 bg-card shadow-lg">
            {/* Picker header */}
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-border">
              <div className="flex gap-2">
                <button
                  onClick={() => setPickerMode('upload')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    pickerMode === 'upload'
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <Upload size={14} className="inline mr-1" />
                  Upload
                </button>
                <button
                  onClick={() => setPickerMode('library')}
                  disabled={!gameId}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    pickerMode === 'library'
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  } ${!gameId ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <Library size={14} className="inline mr-1" />
                  Library
                </button>
              </div>
              <button
                onClick={() => setShowPicker(false)}
                className="p-1 text-muted-foreground hover:text-foreground"
              >
                <X size={16} />
              </button>
            </div>

            {/* Picker content */}
            {pickerMode === 'upload' ? (
              <div className="space-y-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileUpload}
                  accept={accept}
                  className="hidden"
                  id="image-upload-picker"
                  disabled={uploading}
                />
                <label
                  htmlFor="image-upload-picker"
                  className={`w-full h-24 border-2 border-dashed rounded-lg flex items-center justify-center transition-colors cursor-pointer ${
                    uploading
                      ? 'border-border cursor-wait opacity-50'
                      : 'border-border hover:border-primary'
                  }`}
                >
                  <div className="text-center">
                    {uploading ? (
                      <Loader2 size={24} className="mx-auto text-muted-foreground mb-1 animate-spin" />
                    ) : (
                      <Upload size={24} className="mx-auto text-muted-foreground mb-1" />
                    )}
                    <p className="text-sm text-muted-foreground">
                      {uploading ? 'Uploading...' : 'Click to upload or drag and drop'}
                    </p>
                  </div>
                </label>
                {uploadError && (
                  <div className="text-destructive text-sm p-2 bg-destructive/10 border border-destructive/20 rounded">
                    {uploadError}
                  </div>
                )}
              </div>
            ) : (
              <div>
                {loadingLibrary ? (
                  <div className="flex items-center justify-center h-24">
                    <LoadingSpinner className="w-6 h-6" />
                  </div>
                ) : libraryImages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-24 text-center">
                    <Library className="w-8 h-8 text-muted-foreground/50 mb-2" />
                    <p className="text-sm text-muted-foreground">{t('platform.editor.tabbedfileuploadercomponent.no_images_in_library')}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                    {libraryImages.map((image) => (
                      <button
                        key={image.path}
                        onClick={() => handleLibrarySelect(image.url)}
                        className="relative aspect-square rounded-md overflow-hidden border-2 border-border hover:border-primary transition-all"
                      >
                        <img
                          src={image.url}
                          alt={image.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23ddd" width="100" height="100"/></svg>';
                          }}
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Render placeholder when no images
  return (
    <div className="space-y-2">
      {/* Image placeholder */}
      <div 
        className={`relative rounded-lg overflow-hidden bg-muted border-2 border-dashed ${
          disabled ? 'border-border' : 'border-border hover:border-primary cursor-pointer'
        } transition-colors`}
        onClick={() => !disabled && !showPicker && openPicker('upload')}
      >
        <div className="aspect-video max-h-48 flex items-center justify-center">
          <div className="text-center p-6">
            <ImageIcon size={48} className="mx-auto text-muted-foreground/50 mb-3" />
            {disabled ? (
              <p className="text-sm text-muted-foreground">{t('platform.wizards.glossary.not_set')}</p>
            ) : (
              <>
                <p className="text-sm text-muted-foreground font-medium mb-1">Click to add image</p>
                <p className="text-xs text-muted-foreground/70">Upload or choose from library</p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Image picker modal/dropdown */}
      {showPicker && !disabled && (
        <div className="border border-border rounded-lg p-4 bg-card shadow-lg">
          {/* Picker header */}
          <div className="flex items-center justify-between mb-3 pb-2 border-b border-border">
            <div className="flex gap-2">
              <button
                onClick={() => setPickerMode('upload')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  pickerMode === 'upload'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Upload size={14} className="inline mr-1" />
                Upload
              </button>
              <button
                onClick={() => setPickerMode('library')}
                disabled={!gameId}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  pickerMode === 'library'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                } ${!gameId ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <Library size={14} className="inline mr-1" />
                Library
              </button>
            </div>
            <button
              onClick={() => setShowPicker(false)}
              className="p-1 text-muted-foreground hover:text-foreground"
            >
              <X size={16} />
            </button>
          </div>

          {/* Picker content */}
          {pickerMode === 'upload' ? (
            <div className="space-y-3">
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileUpload}
                accept={accept}
                className="hidden"
                id="image-upload-empty"
                disabled={uploading}
              />
              <label
                htmlFor="image-upload-empty"
                className={`w-full h-24 border-2 border-dashed rounded-lg flex items-center justify-center transition-colors cursor-pointer ${
                  uploading
                    ? 'border-border cursor-wait opacity-50'
                    : 'border-border hover:border-primary'
                }`}
              >
                <div className="text-center">
                  {uploading ? (
                    <Loader2 size={24} className="mx-auto text-muted-foreground mb-1 animate-spin" />
                  ) : (
                    <Upload size={24} className="mx-auto text-muted-foreground mb-1" />
                  )}
                  <p className="text-sm text-muted-foreground">
                    {uploading ? 'Uploading...' : 'Click to upload or drag and drop'}
                  </p>
                </div>
              </label>
              {uploadError && (
                <div className="text-destructive text-sm p-2 bg-destructive/10 border border-destructive/20 rounded">
                  {uploadError}
                </div>
              )}
            </div>
          ) : (
            <div>
              {loadingLibrary ? (
                <div className="flex items-center justify-center h-24">
                  <LoadingSpinner className="w-6 h-6" />
                </div>
              ) : libraryImages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-24 text-center">
                  <Library className="w-8 h-8 text-muted-foreground/50 mb-2" />
                  <p className="text-sm text-muted-foreground">{t('platform.editor.tabbedfileuploadercomponent.no_images_in_library')}</p>
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                  {libraryImages.map((image) => (
                    <button
                      key={image.path}
                      onClick={() => handleLibrarySelect(image.url)}
                      className="relative aspect-square rounded-md overflow-hidden border-2 border-border hover:border-primary transition-all"
                    >
                      <img
                        src={image.url}
                        alt={image.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23ddd" width="100" height="100"/></svg>';
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
